import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Chrome, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export default function AdminLogin() {
  const { content } = useSite();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      navigate("/admin", { state: { user: data } });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-screen bg-navy grain flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-10">
          <motion.img
            src={content.logo_url}
            alt="Drywall Local 675"
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 110, damping: 13, delay: 0.15 }}
            className={`h-24 w-auto mx-auto mb-8 ${content.logo_backdrop === "white" ? "bg-white rounded-sm px-3 py-2 shadow-2xl" : "drop-shadow-[0_4px_16px_rgba(0,0,0,0.5)]"}`}
            data-testid="admin-login-logo"
          />
          <h1 className="font-heading text-3xl uppercase text-white tracking-tight" data-testid="admin-login-title">
            Site Admin
          </h1>
          <p className="text-white/50 text-sm mt-2">Sign in to update site content</p>
        </div>

        <form onSubmit={submit} className="bg-white p-8 sm:p-10 border border-white/10" data-testid="admin-login-form">
          {error && (
            <p className="mb-6 text-sm font-semibold text-red-600" data-testid="admin-login-error">{error}</p>
          )}
          <label className="block mb-5">
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-500 block mb-2">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email-input"
              className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-brand transition-colors duration-300"
            />
          </label>
          <label className="block mb-8">
            <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-500 block mb-2">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password-input"
              className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-brand transition-colors duration-300"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="w-full bg-brand hover:bg-brand-dark disabled:opacity-60 text-white text-xs font-bold tracking-[0.2em] uppercase py-4 transition-colors duration-300"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="flex items-center gap-4 my-6">
            <span className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-bold">or</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>
          <button
            type="button"
            onClick={googleLogin}
            data-testid="admin-google-login-button"
            className="w-full inline-flex items-center justify-center gap-3 border border-slate-300 hover:border-brand hover:text-brand text-slate-700 text-xs font-bold tracking-[0.2em] uppercase py-4 transition-colors duration-300"
          >
            <Chrome className="h-4 w-4" />
            Continue with Google
          </button>
        </form>

        <Link
          to="/"
          data-testid="admin-login-back-link"
          className="inline-flex items-center gap-2 mt-8 text-xs font-bold tracking-[0.2em] uppercase text-white/50 hover:text-white transition-colors duration-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to site
        </Link>
      </motion.div>
    </main>
  );
}
